<script setup>
import { computed, ref, watch } from 'vue'

import {
  deVariantData,
  getHeightsForType,
  getDepthsForType,
  deVariantWatts,
} from '../lib/de-variant-radiator'
import { trackEvent } from '../lib/tracking'

const props = defineProps({
  strings: {
    type: Object,
    default: null,
  },
  market: {
    type: String,
    default: '',
  },
})

const typeValue = ref('')
const heightValue = ref('')
const depthValue = ref('')
const elementsValue = ref(1)
const temperatureCondition = ref('70/55')

const copy = computed(() => props.strings?.deVariantRadiator ?? {})

const heights = computed(() => {
  if (!typeValue.value) return []
  return getHeightsForType(typeValue.value)
})

const depths = computed(() => {
  if (!typeValue.value || !heightValue.value) return []
  return getDepthsForType(typeValue.value, Number(heightValue.value))
})

watch(typeValue, () => {
  heightValue.value = ''
  depthValue.value = ''
})

watch(heightValue, () => {
  depthValue.value = ''
})

const watts = computed(() => {
  if (!typeValue.value || !heightValue.value || !depthValue.value) return 0
  return deVariantWatts(
    typeValue.value,
    Number(heightValue.value),
    Number(depthValue.value),
    Number(elementsValue.value),
    temperatureCondition.value
  )
})

const resultLabel = computed(() => {
  const template = copy.value.resultLabelTemplate || 'Heat output at {temp} system temperature'
  return template.replace('{temp}', temperatureCondition.value)
})

watch(watts, (val) => {
  if (val > 0) {
    trackEvent('calculator_de_variant_result', {
      type: typeValue.value,
      height: heightValue.value,
      depth: depthValue.value,
      elements: elementsValue.value,
      temperature: temperatureCondition.value,
      watts: val,
      market: props.market,
    })
  }
})
</script>

<template>
  <section class="de-variant-radiator">
    <p class="de-variant-radiator__description">
      {{
        copy.description ||
        'Calculate heat output for legacy radiator types (steel tube, steel fin, cast iron).'
      }}
    </p>
    <div class="de-variant-radiator__fields">
      <label class="de-variant-radiator__field">
        <span class="de-variant-radiator__label">{{ copy.type || 'Type' }}</span>
        <span class="de-variant-radiator__select">
          <select v-model="typeValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="type in deVariantData.types" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </span>
      </label>
      <label class="de-variant-radiator__field">
        <span class="de-variant-radiator__label">{{ copy.height || 'Height' }}</span>
        <span class="de-variant-radiator__select">
          <select v-model="heightValue" :disabled="!typeValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="h in heights" :key="h" :value="h">{{ h }} mm</option>
          </select>
        </span>
      </label>
      <label class="de-variant-radiator__field">
        <span class="de-variant-radiator__label">{{ copy.depth || 'Depth' }}</span>
        <span class="de-variant-radiator__select">
          <select v-model="depthValue" :disabled="!heightValue">
            <option value="" disabled>{{ copy.select || 'Select' }}</option>
            <option v-for="d in depths" :key="d" :value="d">{{ d }} mm</option>
          </select>
        </span>
      </label>
      <label class="de-variant-radiator__field">
        <span class="de-variant-radiator__label">{{ copy.elements || 'Elements' }}</span>
        <span class="de-variant-radiator__select">
          <select v-model="elementsValue">
            <option v-for="n in deVariantData.elements" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </span>
      </label>
      <label class="de-variant-radiator__field">
        <span class="de-variant-radiator__label">{{
          copy.temperature || 'System Temperature'
        }}</span>
        <span class="de-variant-radiator__select">
          <select v-model="temperatureCondition">
            <option v-for="tc in deVariantData.temperatureConditions" :key="tc" :value="tc">
              {{ tc }}
            </option>
          </select>
        </span>
      </label>
    </div>

    <p class="de-variant-radiator__result-label">
      {{ resultLabel }}
    </p>
    <div class="de-variant-radiator__result">
      <span class="de-variant-radiator__value">{{ watts }}</span>
      <span class="de-variant-radiator__unit">W</span>
    </div>
  </section>
</template>

<style scoped>
.de-variant-radiator {
  display: grid;
  gap: 16px;
  padding: 16px 0;
  border-top: 1px solid #e4eaed;
}

.de-variant-radiator__description {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7b7f;
}

.de-variant-radiator__fields {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

.de-variant-radiator__field {
  display: grid;
  gap: 6px;
  font-size: 0.85rem;
  color: #4e6b6e;
}

.de-variant-radiator__label {
  font-weight: 600;
}

.de-variant-radiator__select select {
  width: 100%;
  height: 36px;
  padding: 8px 40px 8px 10px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background-color: var(--semantic-bg-default);
  color: var(--semantic-text-default);
  font-size: 0.85rem;
  appearance: none;
  outline: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5' fill='none' stroke='%23888' stroke-width='2'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 18px 18px;
}

.de-variant-radiator__select select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.de-variant-radiator__result {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
  border: 1px solid var(--semantic-border-muted);
  border-radius: 2px;
  background: var(--semantic-bg-default);
  font-weight: 700;
  color: #0f2b2f;
  width: fit-content;
  margin: 8px auto 0;
}

.de-variant-radiator__result-label {
  margin: var(--space-64) 0 0;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7b7f;
  text-align: center;
}

.de-variant-radiator__value {
  font-size: 1.2rem;
}

.de-variant-radiator__unit {
  font-size: 0.8rem;
  color: #607276;
}
</style>
